/**
 * Pipeline Orchestrator — Runs all validation stages and produces final verdict.
 *
 * ARCHITECTURE v4.0 — Phase 2, Step 2.9
 *
 * Pipeline flow:
 *   Question → Schema(1) → Graph(2) → Content(3) → Visual(4) → Support(5) → Score(6) → Gate(7) → Verdict
 *
 * Design principles:
 * - N4: Issue Contract governs gates; scoring never overrides blockers
 * - N5: Issue determinism — same input → same issues
 * - N6: No silent data correction in production
 */

import type {
  ValidationIssue,
  PipelineStage,
  PipelineResult,
  PipelineBatchResult,
  GateResult,
  IssueDomain,
  GateVerdict,
  IssueKind,
} from './issueContract'
import {
  evaluateGate,
  deduplicateIssues,
  DEFAULT_GATE_THRESHOLDS,
} from './issueContract'
import { getGateThresholds, isAssessmentEligibleType } from './policyEngine'
import { runSchemaStage } from './stages/schemaStage'
import { runTypeStage } from './stages/typeStage'
import { runContentStage } from './stages/contentStage'
import { runVisualStage } from './stages/visualStage'
import { runSupportStage } from './stages/supportStage'
import { runGraphStage } from './stages/graphStage'
import { runSeedStage } from './stages/seedStage'
import type { LDGraph } from '../ldg/types'
import type { Question } from '../../content'

// ─── Pipeline Configuration ───────────────────────────────────────────────────

export interface PipelineOptions {
  /** Built LDG graph for Stage 2 validation */
  graph?: LDGraph
  /** Skip specific stages (for performance or partial runs) */
  skipStages?: PipelineStage[]
  /** Override gate thresholds */
  thresholds?: typeof DEFAULT_GATE_THRESHOLDS
}

// ─── Single Question Pipeline ─────────────────────────────────────────────────

/**
 * Run the full governance pipeline on a single question.
 *
 * This is the PRIMARY validation entry point for the v4.0 architecture.
 * It replaces the old `validateQuestion` in `unifiedValidator.ts`.
 */
export function runPipeline(
  question: Question,
  options?: PipelineOptions,
): PipelineResult {
  const skipStages = new Set(options?.skipStages ?? [])
  const allIssues: ValidationIssue[] = []
  const stageResults: Record<number, ValidationIssue[]> = {
    1: [],
    2: [],
    3: [],
    4: [],
    5: [],
    6: [],
    7: [],
  }

  // ── Stage 1: Schema Validation ───────────────────────────────────────────

  if (!skipStages.has(1)) {
    const schemaIssues = runSchemaStage(question)
    stageResults[1] = schemaIssues
    allIssues.push(...schemaIssues)
  }

  // ── Stage 1b: Question Type Validation ───────────────────────────────────

  if (!skipStages.has(1)) {
    const typeIssues = runTypeStage(question)
    stageResults[1] = [...(stageResults[1] ?? []), ...typeIssues]
    allIssues.push(...typeIssues)
  }

  // ── Stage 2: Graph Validation ────────────────────────────────────────────

  if (!skipStages.has(2)) {
    const graphIssues = runGraphStage(question, options?.graph)
    stageResults[2] = graphIssues
    allIssues.push(...graphIssues)
  }

  // ── Stage 3: Content Validation ──────────────────────────────────────────

  if (!skipStages.has(3)) {
    const contentIssues = runContentStage(question)
    stageResults[3] = contentIssues
    allIssues.push(...contentIssues)
  }

  // ── Stage 4: Visual Validation ───────────────────────────────────────────

  if (!skipStages.has(4)) {
    const visualIssues = runVisualStage(question)
    stageResults[4] = visualIssues
    allIssues.push(...visualIssues)
  }

  // ── Stage 5: Support Validation ──────────────────────────────────────────

  if (!skipStages.has(5)) {
    const supportIssues = runSupportStage(question)
    stageResults[5] = supportIssues
    allIssues.push(...supportIssues)
  }

  // ── Stage 5b: Seed Consolidation Warnings ────────────────────────────────
  // Surfaces tier/skill/subSkill auto-corrections from the seedConsolidator.
  // Always runs (not skippable) — these are data integrity issues.

  const seedIssues = runSeedStage(question)
  if (seedIssues.length > 0) {
    stageResults[5] = [...(stageResults[5] ?? []), ...seedIssues]
    allIssues.push(...seedIssues)
  }

  // ── Stage 6: Scoring (computed in gate evaluation) ───────────────────────
  // No separate scoring stage — score is computed as part of gate evaluation.
  // This keeps scoring deterministic and tied to the issue set.

  // ── Stage 7: Gate Logic ──────────────────────────────────────────────────

  const dedupedIssues = deduplicateIssues(allIssues)
  const thresholds = options?.thresholds ?? getGateThresholds()
  const gateResult = evaluateGate(dedupedIssues, thresholds)

  // Override assessment eligibility based on question type
  if (
    question.questionType &&
    !isAssessmentEligibleType(question.questionType)
  ) {
    gateResult.assessmentEligible = false
  }

  // ── Compute domain scores ────────────────────────────────────────────────

  const domainScores = computeDomainScores(dedupedIssues)

  return {
    questionId: question.id ?? 'UNKNOWN',
    familyId: question.familyId ?? 'UNKNOWN',
    gate: gateResult,
    stageResults: stageResults as Record<PipelineStage, ValidationIssue[]>,
    domainScores,
    timestamp: Date.now(),
  }
}

// ─── Batch Pipeline ───────────────────────────────────────────────────────────

/**
 * Run the pipeline on a batch of questions.
 * Returns aggregate statistics and per-question results.
 */
export function runPipelineBatch(
  questions: Question[],
  options?: PipelineOptions,
): PipelineBatchResult {
  const results = questions.map((q) => runPipeline(q, options))

  // Aggregate verdicts
  const verdicts: Record<GateVerdict, number> = {
    PASS: 0,
    FAIL: 0,
    REVIEW_NEEDED: 0,
  }
  let productionEligible = 0
  let assessmentEligible = 0
  let reviewNeeded = 0
  let totalScore = 0

  const issuesByDomain: Record<string, number> = {}
  const issuesByKind: Record<string, number> = { blocker: 0, coach: 0, info: 0 }

  for (const result of results) {
    verdicts[result.gate.verdict]++
    if (result.gate.productionEligible) productionEligible++
    if (result.gate.assessmentEligible) assessmentEligible++
    if (result.gate.reviewNeeded) reviewNeeded++
    totalScore += result.gate.score

    for (const issue of result.gate.issues) {
      issuesByDomain[issue.domain] = (issuesByDomain[issue.domain] ?? 0) + 1
      issuesByKind[issue.kind] = (issuesByKind[issue.kind] ?? 0) + 1
    }
  }

  return {
    generatedAt: new Date().toISOString(),
    totalQuestions: questions.length,
    verdicts,
    productionEligible,
    assessmentEligible,
    reviewNeeded,
    avgScore:
      questions.length > 0 ? Math.round(totalScore / questions.length) : 100,
    issuesByDomain: issuesByDomain as Record<IssueDomain, number>,
    issuesByKind: issuesByKind as Record<IssueKind, number>,
    results,
  }
}

// ─── Compatibility Alias ──────────────────────────────────────────────────────

/**
 * Alias for runPipeline — used by GovernancePage and legacy callers.
 */
export const runGovernancePipeline = runPipeline

// ─── Domain Score Computation ─────────────────────────────────────────────────

function computeDomainScores(
  issues: ValidationIssue[],
): Record<IssueDomain, number> {
  const domains: IssueDomain[] = [
    'schema',
    'type',
    'graph',
    'content',
    'visual',
    'support',
    'scoring',
    'gate',
  ]
  const scores: Record<string, number> = {}

  for (const domain of domains) {
    const domainIssues = issues.filter((i) => i.domain === domain)
    if (domainIssues.length === 0) {
      scores[domain] = 100
      continue
    }

    const penalty = domainIssues.reduce((sum, issue) => {
      switch (issue.kind) {
        case 'blocker':
          return sum + 30
        case 'coach':
          return sum + Math.round(issue.severity / 4)
        case 'info':
          return sum + 2
        default:
          return sum
      }
    }, 0)

    scores[domain] = Math.max(0, 100 - penalty)
  }

  return scores as Record<IssueDomain, number>
}
